// ══════════════════════════════════════════════════════════════
// API DATA LAYER — replaces Firestore (src/firebase.js).
// Talks to the DinkTank.Api dev server; live updates via SignalR.
// ══════════════════════════════════════════════════════════════
import * as signalR from '@microsoft/signalr';

// '' (set at build time) means same-origin — the API serves the SPA in production.
export const API_BASE = process.env.REACT_APP_API_BASE ?? 'http://localhost:5141';

// ---- token storage -------------------------------------------------------
const TOKENS_KEY = 'dt_tokens';
let tokens = null;
try { tokens = JSON.parse(localStorage.getItem(TOKENS_KEY)); } catch { /* ignore */ }

function setTokens(next) {
  tokens = next;
  try {
    if (next) localStorage.setItem(TOKENS_KEY, JSON.stringify(next));
    else localStorage.removeItem(TOKENS_KEY);
  } catch { /* ignore */ }
}

export function hasSession() { return !!tokens?.accessToken; }

// ---- fetch helper with automatic refresh ---------------------------------
async function refreshTokens() {
  if (!tokens?.refreshToken) return false;
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });
  if (!res.ok) { setTokens(null); return false; }
  setTokens(await res.json());
  return true;
}

async function apiFetch(path, { method = 'GET', body, retry = true } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (tokens?.accessToken) headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && retry && tokens?.refreshToken) {
    if (await refreshTokens()) return apiFetch(path, { method, body, retry: false });
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${method} ${path} -> ${res.status}${text ? `: ${text}` : ''}`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const contentType = res.headers.get('content-type') || '';
  return contentType.includes('json') ? res.json() : res.text();
}

// ---- auth ----------------------------------------------------------------
export async function login(identifier, password) {
  const res = await fetch(`${API_BASE}/api/auth/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });
  // 423 = Identity lockout. Surface it so the screen can say so instead of
  // blaming the password (which sent people into a re-locking retry loop).
  if (res.status === 423) {
    const body = await res.json().catch(() => ({}));
    return { lockedOut: true, minutes: body.minutes || 5 };
  }
  if (!res.ok) return null;
  setTokens(await res.json());
  return me();
}

export async function me() {
  const u = await apiFetch('/api/users/me');
  return { id: u.id, username: u.username, email: u.email, role: u.role, phone: u.phone, smsAlerts: u.smsAlerts };
}

export async function logout() {
  // Record the sign-out (best-effort) before discarding the token.
  try { await apiFetch('/api/auth/logout-event', { method: 'POST' }); } catch { /* ignore */ }
  setTokens(null);
}

export async function anyUsersExist() {
  const r = await apiFetch('/api/users/any');
  return !!r.any;
}

export async function bootstrapAdmin(username, email, password) {
  await apiFetch('/api/users', { method: 'POST', body: { username, email, password, role: 'superadmin' } });
  return login(email, password);
}

// ---- users (admin) -------------------------------------------------------
export const listUsers = () => apiFetch('/api/users');
export const addUser = (u) => apiFetch('/api/users', { method: 'POST', body: { username: u.username, email: u.email, password: u.password, role: u.role, phone: u.phone || null, smsAlerts: u.smsAlerts ?? null } });
export const updateUser = (u) => apiFetch(`/api/users/${u.id}`, {
  method: 'PUT',
  body: { username: u.username, email: u.email || null, password: u.password || null, role: u.role, phone: u.phone ?? null, smsAlerts: u.smsAlerts ?? null },
});

// ---- activity log (superadmin) -------------------------------------------
export const listActivity = (take = 100) => apiFetch(`/api/activity?take=${take}`);
export const deleteUser = (id) => apiFetch(`/api/users/${id}`, { method: 'DELETE' });
export const unlockUser = (id) => apiFetch(`/api/users/${id}/unlock`, { method: 'POST' });
export const setMyEmail = (email) => apiFetch('/api/users/me/email', { method: 'PUT', body: { email } });

// ---- players / courts ----------------------------------------------------
export const listPlayers = () => apiFetch('/api/players');
export const addPlayer = (p) => apiFetch('/api/players', { method: 'POST', body: p });
export const updatePlayer = (p) => apiFetch(`/api/players/${p.id}`, { method: 'PUT', body: p });
export const deletePlayer = (id) => apiFetch(`/api/players/${id}`, { method: 'DELETE' });

export const listCourts = () => apiFetch('/api/courts');
export const addCourt = (c) => apiFetch('/api/courts', { method: 'POST', body: c });
export const deleteCourt = (id) => apiFetch(`/api/courts/${id}`, { method: 'DELETE' });

// ---- tournaments ---------------------------------------------------------
export const listTournaments = () => apiFetch('/api/tournaments');
export const getTournament = (id) => apiFetch(`/api/tournaments/${id}`);
export const saveTournament = (t) => apiFetch(`/api/tournaments/${t.id}`, { method: 'PUT', body: t });
export const deleteTournament = (id) => apiFetch(`/api/tournaments/${id}`, { method: 'DELETE' });
export const registerForTournament = (id, entry) =>
  apiFetch(`/api/tournaments/${id}/register`, { method: 'POST', body: entry });

// ---- leagues -------------------------------------------------------------
export const listLeagues = () => apiFetch('/api/leagues');
export const getLeague = (id) => apiFetch(`/api/leagues/${id}`);
export const addLeague = (l) => apiFetch('/api/leagues', { method: 'POST', body: l });
export const updateLeague = (id, l) => apiFetch(`/api/leagues/${id}`, { method: 'PUT', body: l });
export const deleteLeague = (id) => apiFetch(`/api/leagues/${id}`, { method: 'DELETE' });
export const createSeason = (leagueId, s) => apiFetch(`/api/leagues/${leagueId}/seasons`, { method: 'POST', body: s });
export const getSeason = (id) => apiFetch(`/api/seasons/${id}`);
export const updateSeason = (id, s) => apiFetch(`/api/seasons/${id}`, { method: 'PUT', body: s });
export const setSeasonStatus = (id, status) => apiFetch(`/api/seasons/${id}/status`, { method: 'POST', body: { status } });
export const getStandings = (id) => apiFetch(`/api/seasons/${id}/standings`);
export const addTeam = (seasonId, t) => apiFetch(`/api/seasons/${seasonId}/teams`, { method: 'POST', body: t });
export const generateSchedule = (seasonId) => apiFetch(`/api/seasons/${seasonId}/schedule/generate`, { method: 'POST' });
export const seedPlayoffs = (seasonId) => apiFetch(`/api/seasons/${seasonId}/playoffs/seed`, { method: 'POST' });
export const updateTeam = (id, t) => apiFetch(`/api/teams/${id}`, { method: 'PUT', body: t });
export const deleteTeam = (id) => apiFetch(`/api/teams/${id}`, { method: 'DELETE' });
export const addMember = (teamId, m) => apiFetch(`/api/teams/${teamId}/members`, { method: 'POST', body: m });
export const updateMember = (teamId, memberId, m) => apiFetch(`/api/teams/${teamId}/members/${memberId}`, { method: 'PUT', body: m });
export const deleteMember = (teamId, memberId) => apiFetch(`/api/teams/${teamId}/members/${memberId}`, { method: 'DELETE' });
export const getFixture = (id) => apiFetch(`/api/fixtures/${id}`);
export const setLineup = (fixtureId, body) => apiFetch(`/api/fixtures/${fixtureId}/lineup`, { method: 'PUT', body });
export const generatePairings = (fixtureId) => apiFetch(`/api/fixtures/${fixtureId}/pairings/generate`, { method: 'POST' });
export const finalizeFixture = (fixtureId) => apiFetch(`/api/fixtures/${fixtureId}/finalize`, { method: 'POST' });
export const scoreGame = (gameId, homeScore, awayScore) => apiFetch(`/api/games/${gameId}/score`, { method: 'PUT', body: { homeScore, awayScore } });
export const forfeitGame = (gameId, toSide) => apiFetch(`/api/games/${gameId}/forfeit`, { method: 'POST', body: { toSide } });
export const substitute = (gameId, body) => apiFetch(`/api/games/${gameId}/substitution`, { method: 'POST', body });

// ---- realtime (SignalR replaces Firestore onSnapshot) ---------------------
// connectRealtime returns a stop() function. Handlers:
//   onTournamentsChanged() — any tournament was saved/deleted (refetch list)
//   onPlayersChanged() / onCourtsChanged()
//   onTournamentUpdated(dto) — a watched tournament changed (via watch())
export function connectRealtime(handlers = {}) {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_BASE}/hubs/tournament`)
    .withAutomaticReconnect()
    .build();

  if (handlers.onTournamentsChanged) connection.on('TournamentsChanged', handlers.onTournamentsChanged);
  if (handlers.onPlayersChanged) connection.on('PlayersChanged', handlers.onPlayersChanged);
  if (handlers.onCourtsChanged) connection.on('CourtsChanged', handlers.onCourtsChanged);
  if (handlers.onTournamentUpdated) connection.on('TournamentUpdated', handlers.onTournamentUpdated);
  if (handlers.onLeaguesChanged) connection.on('LeaguesChanged', handlers.onLeaguesChanged);
  if (handlers.onLeagueFixtureUpdated) connection.on('LeagueFixtureUpdated', handlers.onLeagueFixtureUpdated);

  const watched = new Set();
  const watchedFixtures = new Set();
  const start = connection.start().catch(err => console.error('SignalR connect failed', err));
  connection.onreconnected(() => {
    watched.forEach(id => connection.invoke('WatchTournament', id).catch(() => {}));
    watchedFixtures.forEach(id => connection.invoke('WatchFixture', id).catch(() => {}));
  });

  return {
    watch: async (tournamentId) => {
      watched.add(tournamentId);
      await start;
      if (connection.state === signalR.HubConnectionState.Connected)
        await connection.invoke('WatchTournament', tournamentId).catch(() => {});
    },
    unwatch: async (tournamentId) => {
      watched.delete(tournamentId);
      if (connection.state === signalR.HubConnectionState.Connected)
        await connection.invoke('UnwatchTournament', tournamentId).catch(() => {});
    },
    watchFixture: async (fixtureId) => {
      watchedFixtures.add(fixtureId);
      await start;
      if (connection.state === signalR.HubConnectionState.Connected)
        await connection.invoke('WatchFixture', fixtureId).catch(() => {});
    },
    unwatchFixture: async (fixtureId) => {
      watchedFixtures.delete(fixtureId);
      if (connection.state === signalR.HubConnectionState.Connected)
        await connection.invoke('UnwatchFixture', fixtureId).catch(() => {});
    },
    stop: () => connection.stop(),
  };
}
